import { APP_CONFIG } from '@/config/app.config'
import { env } from '@/config/env'
import { refreshSession } from '@/lib/http/session-fetch'
import type {
  ChatClientEvent,
  ChatClientFrame,
  ChatResultFrame,
  ChatServerFrame,
} from '@/api/chat/chat-types'

export type ChatServerMessage = ChatClientEvent
export type ChatResultMessage = ChatResultFrame

type Listener = (event: ChatClientEvent) => void

const UNAUTHORIZED_CLOSE_CODE = 4401

const SERVER_FRAME_TYPES = new Set([
  'ready',
  'chat.attached',
  'chat.titled',
  'chat.demoted',
  'status',
  'question',
  'result',
  'answer',
  'aborted',
  'error',
])

function chatUrl(): string {
  const url = new URL(env.apiBaseUrl, window.location.origin)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/$/, '')}/chat`
  url.search = ''
  url.hash = ''
  return url.toString()
}

function parseFrame(data: unknown): ChatServerFrame | null {
  try {
    const value: unknown = JSON.parse(String(data))
    if (
      typeof value !== 'object' ||
      value === null ||
      !('type' in value) ||
      typeof value.type !== 'string' ||
      !SERVER_FRAME_TYPES.has(value.type)
    ) {
      return null
    }
    return value as ChatServerFrame
  } catch {
    return null
  }
}

export class ChatClient {
  private socket: WebSocket | null = null
  private readyPromise: Promise<void> | null = null
  private resolveReady: (() => void) | null = null
  private rejectReady: ((error: Error) => void) | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private generation = 0
  private manuallyClosed = false
  private desiredChatId: string | null = null
  private attachedChatId: string | null = null
  private writer = false
  private inFlightRequestId: string | null = null
  private listeners = new Set<Listener>()
  private attachQueue: Promise<void> = Promise.resolve()
  private authRetryUsed = false

  connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN && !this.resolveReady) {
      return Promise.resolve()
    }
    if (this.readyPromise) return this.readyPromise

    this.manuallyClosed = false
    const generation = ++this.generation
    const socket = new WebSocket(chatUrl())
    this.socket = socket
    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve
      this.rejectReady = reject
    })
    const connectTimer = setTimeout(() => {
      if (socket.readyState === WebSocket.CONNECTING) socket.close()
    }, APP_CONFIG.chat.connectTimeoutMs)

    socket.addEventListener('open', () => clearTimeout(connectTimer))
    socket.addEventListener('message', (event) => {
      if (generation !== this.generation) return
      const frame = parseFrame(event.data)
      if (!frame) return
      this.handleFrame(frame)
    })
    socket.addEventListener('error', () => {
      clearTimeout(connectTimer)
      if (generation !== this.generation || !this.rejectReady) return
      this.rejectReady(new Error('Could not connect to the orchestration service.'))
      this.clearReadyPromise()
    })
    socket.addEventListener('close', (event) => {
      clearTimeout(connectTimer)
      if (generation !== this.generation) return
      this.socket = null
      this.writer = false
      this.attachedChatId = null
      this.inFlightRequestId = null
      this.rejectReady?.(new Error('The chat connection closed.'))
      this.clearReadyPromise()
      this.emit({ type: 'connection.closed' })
      if (event.code === UNAUTHORIZED_CLOSE_CODE) {
        void this.recoverFromUnauthorized()
        return
      }
      this.authRetryUsed = false
      if (!this.manuallyClosed && this.desiredChatId) this.scheduleReconnect()
    })

    return this.readyPromise
  }

  attach(chatId: string): Promise<void> {
    this.desiredChatId = chatId
    const operation = this.attachQueue
      .catch(() => undefined)
      .then(() => this.performAttach(chatId))
    this.attachQueue = operation
    return operation
  }

  private async performAttach(chatId: string): Promise<void> {
    await this.connect()
    if (this.desiredChatId !== chatId) throw new Error('Chat attachment was superseded.')
    if (this.attachedChatId === chatId && this.writer) return

    await new Promise<void>((resolve, reject) => {
      const unsubscribe = this.subscribe((event) => {
        if (event.type === 'chat.attached' && event.chat_id === chatId) {
          unsubscribe()
          resolve()
        } else if (
          event.type === 'error' &&
          event.request_id === null &&
          event.detail.code !== 'unauthorized' &&
          this.desiredChatId === chatId
        ) {
          unsubscribe()
          reject(new Error(event.detail.error))
        } else if (event.type === 'connection.closed') {
          unsubscribe()
          reject(new Error('The chat connection closed.'))
        }
      })
      this.sendFrame({ type: 'chat.attach', chat_id: chatId })
    })
  }

  sendMessage(text: string, scenarioId?: string): string {
    if (!this.attachedChatId || !this.writer) {
      throw new Error('Attach a chat before sending a message.')
    }
    if (this.inFlightRequestId) throw new Error('A chat request is already running.')

    const requestId = crypto.randomUUID()
    this.inFlightRequestId = requestId
    this.sendFrame({
      type: 'message.send',
      request_id: requestId,
      text,
      ...(scenarioId ? { scenario_id: scenarioId } : {}),
    })
    return requestId
  }

  takeOver(): Promise<void> {
    if (!this.desiredChatId) return Promise.reject(new Error('No chat is selected.'))
    this.writer = false
    this.attachedChatId = null
    return this.attach(this.desiredChatId)
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  disconnect(): void {
    this.manuallyClosed = true
    this.authRetryUsed = false
    this.desiredChatId = null
    this.attachedChatId = null
    this.writer = false
    this.inFlightRequestId = null
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.emit({ type: 'connection.closed' })
    this.generation += 1
    this.socket?.close()
    this.socket = null
    this.rejectReady?.(new Error('The chat connection closed.'))
    this.clearReadyPromise()
  }

  private handleFrame(frame: ChatServerFrame): void {
    switch (frame.type) {
      case 'ready': {
        this.authRetryUsed = false
        const resolve = this.resolveReady
        this.clearReadyPromise()
        resolve?.()
        break
      }
      case 'chat.attached':
        this.attachedChatId = frame.chat_id
        this.writer = true
        break
      case 'chat.demoted':
        if (frame.chat_id === this.attachedChatId) this.writer = false
        break
      case 'question':
      case 'result':
      case 'answer':
      case 'aborted':
        if (frame.request_id === this.inFlightRequestId) this.inFlightRequestId = null
        break
      case 'error':
        if (frame.request_id === this.inFlightRequestId) this.inFlightRequestId = null
        // The close handler owns unauthorized recovery, but clearing the
        // in-flight id must not depend on a close arriving.
        if (frame.detail.code === 'unauthorized') return
        if (frame.detail.code === 'not_writer') this.writer = false
        if (frame.detail.code === 'chat_not_found' && frame.request_id) {
          this.desiredChatId = null
          this.attachedChatId = null
          this.writer = false
        }
        break
      default:
        break
    }
    this.emit(frame)
  }

  private sendFrame(frame: ChatClientFrame): void {
    if (this.socket?.readyState !== WebSocket.OPEN || this.resolveReady) {
      throw new Error('The chat connection is not ready.')
    }
    this.socket.send(JSON.stringify(frame))
  }

  private emit(event: ChatClientEvent): void {
    this.listeners.forEach((listener) => listener(event))
  }

  private clearReadyPromise(): void {
    this.readyPromise = null
    this.resolveReady = null
    this.rejectReady = null
  }

  private async recoverFromUnauthorized(): Promise<void> {
    const chatId = this.desiredChatId

    if (this.authRetryUsed) {
      this.desiredChatId = null
      return
    }

    this.authRetryUsed = true
    const refreshed = await refreshSession()
    if (!refreshed || this.manuallyClosed) {
      this.desiredChatId = null
      return
    }

    if (chatId) await this.attach(chatId).catch(() => undefined)
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      const chatId = this.desiredChatId
      if (!chatId || this.manuallyClosed) return
      void this.attach(chatId).catch(() => this.scheduleReconnect())
    }, 1000)
  }
}

export const chatClient = new ChatClient()
